import { TABLES } from '@/utils/constants';
import {
  getRecord,
  getRecords,
  insertRecord,
  updateRecord
} from '@/utils/helpers/data';
import { Access } from '@/utils/types';

export const COLUMNS = {
  ID: 'id',
  FIRST_NAME: 'first_name',
  LAST_NAME: 'last_name',
  CLINIC_ID: 'clinic_id',
  CREATED_AT: 'created_at',
  UPDATED_AT: 'updated_at',
  MOBILE_NUMBER: 'mobile_number',
  USER_ID: 'user_id',
  ACCESS: 'access',
  PATIENT_VERIFICATION_ENABLED: 'patient_verification_enabled',
  SPECIALIZATION: 'specialization'
};

export interface Dentist {
  id?: string;
  first_name: string;
  last_name: string;
  clinic_id: string;
  created_at?: string;
  updated_at?: string;
  mobile_number?: string;
  user_id?: string;
  access?: Access | null;
  patient_verification_enabled?: boolean;
  specialization?: string | null;
}

/**
 * Creates a new dentist record in the database.
 * @param dentist - The dentist data to insert.
 * @returns The created dentist or null if insertion failed.
 * @throws Will throw an error if the insertion fails.
 */
export const createDentist = async (
  dentist: Dentist
): Promise<Dentist | null> => {
  return insertRecord<Dentist>(TABLES.DENTISTS, dentist);
};

/**
 * Updates an existing dentist record in the database.
 * @param dentistId - The ID of the dentist to update.
 * @param dentist - The updated dentist data.
 * @returns The updated dentist or null if update failed.
 * @throws Will throw an error if the update fails.
 */
export const updateDentist = async ({
  dentistId,
  dentist
}: {
  dentistId: string | null;
  dentist: Dentist;
}): Promise<Dentist | null> => {
  if (!dentistId) return null;
  return updateRecord<Dentist>(TABLES.DENTISTS, COLUMNS.ID, dentistId, dentist);
};

/**
 * Retrieves a single dentist by their ID.
 * @param dentistId - The ID of the dentist to retrieve.
 * @returns The requested dentist or null if not found.
 * @throws Will throw an error if the retrieval fails.
 */
export const getDentist = async (
  dentistId: string | null
): Promise<Dentist | null> => {
  if (!dentistId) return null;
  return getRecord<Dentist>(TABLES.DENTISTS, COLUMNS.ID, dentistId);
};

/**
 * Retrieves all dentists associated with a specific clinic ID.
 * @param clinicId - The ID of the clinic.
 * @returns An array of dentists.
 * @throws Will throw an error if the retrieval fails.
 */
export const getDentistsByClinicId = async (
  clinicId: string | null
): Promise<Dentist[]> => {
  if (!clinicId) return [];
  return getRecords<Dentist>(TABLES.DENTISTS, COLUMNS.CLINIC_ID, clinicId);
};
